import { FC, useEffect, useMemo, useState } from 'react'
import { capitalize } from 'lodash'

import { VStack } from '@revolut-internal/ui-kit'

import { ChartTableSkeleton } from 'components/ChartTableSkeleton'
import { LineChart } from 'components/LineChart/LineChart'
import { PageSection } from 'components/PageSection/PageSection'
import { useChannel } from 'hooks/useChannel'
import { usePrepareChartColorMap } from 'hooks/usePrepareChartColorMap'
import { useSelectedCharts } from 'hooks/useSelectedCharts'
import { useTicketsVolumeQuery } from 'modules/api/analytics/queries'
import { UrlStateStorageKeys } from 'modules/url-state-storage/consts'
import { useHashedSearchParams } from 'modules/url-state-storage/useHashedSearchParams'
import { VolumeAggregationDimensionExtended } from 'types'
import { mergeChartAdditionData } from 'utils/charts/mergeChartAdditionData'
import { prepareChartData } from 'utils/charts/prepareChartData'

import { useAdditionalFilters } from '../../GlobalFilters/AdditionalFilters/AdditionalFiltersContext'
import { GlobalFilterState } from '../../GlobalFilters/GlobalFilters'

import { ComparisonTypeLabels, xAxisLabelByTimeScale } from './consts'
import { useVerifyChannelAndDimensionCoherence } from './hooks'
import {
  TicketsVolumeDetailsProvider,
  useTicketsVolumeDetails,
} from './TicketsVolumeDetailsContext/TicketsVolumeDetailsContext'
import { TicketsVolumeDetailsControlsBar } from './TicketsVolumeDetailsControlsBar'
import { TicketsVolumeDetailsTable } from './TicketsVolumeDetailsTable'

export type AggregationDimensionValue = {
  dimension: VolumeAggregationDimensionExtended
  dimensionLabel: string
  dimensionTarget?: string
}

const TicketsVolumeDetailsInner: FC = () => {
  const [colorMapResettingState, setColorMapResettingState] = useState<
    'idle' | 'awaiting'
  >('idle')

  const { channel, regionedChannel, entityName } = useChannel()

  const { selectedCharts, handleChartsSelect } = useSelectedCharts(
    UrlStateStorageKeys.TicketVolumeTableSelectedCharts,
  )
  const { state: globalFiltersState } = useHashedSearchParams<GlobalFilterState>({
    hashParamKey: UrlStateStorageKeys.GlobalFilters,
    contextKey: regionedChannel,
  })

  const {
    currentTimeRange,
    timeScale,
    comparisonType,
    aggregationDimension,
    dimensionTarget,
  } = useTicketsVolumeDetails()

  const verifyChannelAndDimensionCoherence = useVerifyChannelAndDimensionCoherence()
  const { preparedFiltersForRequest } = useAdditionalFilters()

  const queueId = globalFiltersState.queues ?? []
  const specialisationId = globalFiltersState.specialisations ?? []
  const departmentId = globalFiltersState.departments ?? []
  const areaId = globalFiltersState.areas ?? []

  const shouldRequestTicketsVolume = useMemo(
    () =>
      Boolean(currentTimeRange) &&
      verifyChannelAndDimensionCoherence(
        aggregationDimension.dimension,
        aggregationDimension.dimensionTarget,
      ),
    [aggregationDimension, currentTimeRange, verifyChannelAndDimensionCoherence],
  )

  const {
    data: ticketVolume = [],
    isPending: isPendingData,
    isFetching: isFetchingData,
    isFetched: isFetchedData,
  } = useTicketsVolumeQuery(
    {
      ...currentTimeRange,
      departmentId,
      areaId,
      specialisationId,
      queueId,
      scale: timeScale,
      dimension: aggregationDimension.dimension,
      dimensionTarget,
      channel: [channel],
      filter: preparedFiltersForRequest,
    },
    { enabled: shouldRequestTicketsVolume },
  )
  const { data: ticketVolumeAddition = [], isPending: isPendingAdditionData } =
    useTicketsVolumeQuery(
      {
        ...currentTimeRange,
        departmentId,
        areaId,
        specialisationId,
        queueId,
        scale: timeScale,
        dimension: aggregationDimension.dimension,
        dimensionTarget,
        comparisonType,
        channel: [channel],
        filter: preparedFiltersForRequest,
      },
      {
        enabled: shouldRequestTicketsVolume,
      },
    )
  const isPending = isPendingData || isPendingAdditionData

  const { colorMap, resetColorMap } = usePrepareChartColorMap(
    'dimensionValue',
    ticketVolume,
    ticketVolumeAddition,
  )

  useEffect(
    function resetColorsMapIfDataAlreadyLoadedOrPlanItOtherwise() {
      if (colorMapResettingState !== 'idle') {
        return
      }

      if (isFetchedData) {
        resetColorMap()
      } else {
        setColorMapResettingState('awaiting')
      }
    },
    // we need to trigger it only when aggregationDimension changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [aggregationDimension.dimensionTarget, aggregationDimension.dimension, channel],
  )

  useEffect(
    function resetColorsMapWhenDataIsLoaded() {
      if (colorMapResettingState !== 'awaiting' || isFetchingData) {
        return
      }

      resetColorMap()
      setColorMapResettingState('idle')
    },

    // we need to trigger it only when isFetchingData changes to false
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFetchingData],
  )

  const { chartData: basicChartData, charts: lines } = useMemo(
    () =>
      prepareChartData({
        data: ticketVolume,
        dataKey: 'dimensionValue',
        dataCountKey: 'volume',
        labelTimeFormat: xAxisLabelByTimeScale[timeScale],
        colorMap,
      }),
    [ticketVolume, timeScale, colorMap],
  )
  const { chartData: additionChartData, charts: additionLines } = useMemo(
    () =>
      prepareChartData({
        data: ticketVolumeAddition,
        dataKey: 'dimensionValue',
        dataCountKey: 'volume',
        labelTimeFormat: xAxisLabelByTimeScale[timeScale],
        colorMap,
        isAddition: true,
      }),
    [ticketVolumeAddition, timeScale, colorMap],
  )
  const chartData = useMemo(
    () => mergeChartAdditionData(basicChartData, additionChartData),
    [basicChartData, additionChartData],
  )
  const chartLines = [...lines, ...additionLines]

  return (
    <PageSection title={`${capitalize(entityName)} volume breakdown`}>
      <VStack space="s-32">
        <TicketsVolumeDetailsControlsBar />

        {isPending ? (
          <ChartTableSkeleton />
        ) : (
          <>
            <LineChart
              data={chartData}
              charts={lines}
              additionLabel={ComparisonTypeLabels[comparisonType]}
              additionCharts={additionLines}
              selectedCharts={selectedCharts}
              entityName={aggregationDimension.dimensionLabel}
            />

            <TicketsVolumeDetailsTable
              data={basicChartData}
              charts={chartLines}
              additionalData={additionChartData}
              additionalDataLabel={ComparisonTypeLabels[comparisonType]}
              selectedCharts={selectedCharts}
              entityName={aggregationDimension.dimensionLabel}
              onSelectCharts={handleChartsSelect}
            />
          </>
        )}
      </VStack>
    </PageSection>
  )
}

export const TicketsVolumeDetails = () => (
  <TicketsVolumeDetailsProvider>
    <TicketsVolumeDetailsInner />
  </TicketsVolumeDetailsProvider>
)
